import {
  Gavel, Home as HomeIcon, Users, Landmark, Scale, ShieldCheck, Leaf, Coins, FileText,
  Heart, Briefcase, Car, BookOpen,
} from "lucide-react";

/**
 * 10 umbrella category groups — each with keyword matchers, display names,
 * Tailwind chip classes, gradient classes, and an icon.
 */
export const UMBRELLA_GROUPS = [
  {
    name: "Criminal & Bail",
    nameHi: "आपराधिक एवं जमानत",
    keywords: ["criminal", "bail", "ndps", "murder", "arrest", "crpc", "ipc", "arms", "terror", "pocso", "prevention of corruption"],
    chip: "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700",
    grad: "from-rose-600 to-orange-500",
    icon: ShieldCheck,
  },
  {
    name: "Constitutional Law",
    nameHi: "संवैधानिक कानून",
    keywords: ["constitution", "fundamental", "writ", "pil", "public interest", "article 21", "article 14", "article 32", "article 226"],
    chip: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700",
    grad: "from-amber-500 to-orange-500",
    icon: Landmark,
  },
  {
    name: "Service & Employment",
    nameHi: "सेवा एवं रोज़गार",
    keywords: ["service", "employ", "labour", "labor", "workmen", "regulariz", "government employee", "central administrative"],
    chip: "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-700",
    grad: "from-teal-500 to-emerald-500",
    icon: Users,
  },
  {
    name: "Family & Succession",
    nameHi: "पारिवारिक एवं उत्तराधिकार",
    keywords: ["family", "matrimon", "succession", "custody", "maintenance", "divorce", "guardianship", "coparcenary", "inheritance", "adoption"],
    chip: "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-700",
    grad: "from-violet-500 to-fuchsia-500",
    icon: Heart,
  },
  {
    name: "Property & Land",
    nameHi: "संपत्ति एवं भूमि",
    keywords: ["property", "land", "acquisition", "rent", "eviction", "landlord", "tenant", "partition", "real estate"],
    chip: "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700",
    grad: "from-orange-500 to-amber-500",
    icon: HomeIcon,
  },
  {
    name: "Commercial & Company",
    nameHi: "वाणिज्यिक एवं कंपनी",
    keywords: ["contract", "company", "insolvency", "arbitration", "ibc", "commercial", "banking", "cheque", "dishonour", "npa"],
    chip: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700",
    grad: "from-blue-500 to-indigo-500",
    icon: Briefcase,
  },
  {
    name: "Tax & Revenue",
    nameHi: "कर एवं राजस्व",
    keywords: ["tax", "gst", "customs", "income tax", "stamp duty", "excise", "finance"],
    chip: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700",
    grad: "from-emerald-500 to-green-500",
    icon: Coins,
  },
  {
    name: "Motor & Insurance",
    nameHi: "मोटर एवं बीमा",
    keywords: ["motor", "accident", "insurance", "vehicle", "mv act", "compensation claim"],
    chip: "bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-700",
    grad: "from-cyan-500 to-teal-500",
    icon: Car,
  },
  {
    name: "Environmental & PIL",
    nameHi: "पर्यावरण एवं जनहित",
    keywords: ["environment", "pollution", "forest", "wildlife"],
    chip: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700",
    grad: "from-green-500 to-emerald-500",
    icon: Leaf,
  },
  {
    name: "Civil & Procedure",
    nameHi: "सिविल एवं प्रक्रिया",
    keywords: ["civil", "limitation", "evidence", "execution", "decree"],
    chip: "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700",
    grad: "from-sky-500 to-indigo-500",
    icon: BookOpen,
  },
];

/**
 * Returns the umbrella group that an area_of_law string belongs to.
 * Falls back to null if no match.
 */
export function getGroup(area = "") {
  const a = (area || "").toLowerCase();
  for (const g of UMBRELLA_GROUPS) {
    if (g.keywords.some((k) => a.includes(k))) return g;
  }
  return null;
}

/**
 * Legacy: Maps an "area of law" string to colourful, consistent styling + an icon.
 * Used on cards and chips.
 */
export function areaStyle(area = "") {
  const g = getGroup(area);
  if (g) return { chip: g.chip, grad: g.grad, icon: g.icon };
  return {
    chip: "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300",
    grad: "from-sky-500 to-indigo-500",
    icon: Scale,
  };
}

// Colourful gradients used to give each carousel slide variety.
export const SLIDE_GRADS = [
  "from-teal-600 to-emerald-500",
  "from-indigo-600 to-blue-500",
  "from-rose-600 to-orange-500",
  "from-violet-600 to-fuchsia-500",
  "from-amber-500 to-orange-600",
  "from-cyan-600 to-teal-500",
  "from-sky-600 to-indigo-600",
  "from-fuchsia-600 to-pink-500",
];
